import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd } from "../lib/format";
import { Avatar, Empty } from "../components/ui";
import * as work from "../lib/workClient";

/* Churn & expansion prediction board. Every account scored, ranked by churn
 * risk, each row showing the two probabilities and the single biggest driver
 * behind the risk — the explainable "why". Open an account to see the full
 * driver breakdown in the 360's prediction widget. */

type Lens = "churn" | "expansion";
const riskColor = (v: number) => (v >= 60 ? "var(--risk)" : v >= 35 ? "var(--watch)" : "var(--good)");
const bandClass = (b: string) => (b === "high" ? "risk" : b === "medium" ? "watch" : "good");

function Bar({ value, color }: { value: number; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "var(--line)", borderRadius: 4, overflow: "hidden", minWidth: 60 }}>
        <span style={{ display: "block", height: "100%", width: `${value}%`, background: color }} />
      </div>
      <b style={{ fontSize: 13, color, width: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{value}%</b>
    </div>
  );
}

export default function Predictions() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [preds, setPreds] = useState<work.Prediction[]>([]);
  const [lens, setLens] = useState<Lens>("churn");

  useEffect(() => { work.loadPredictions(ws).then((r) => { setMode(r.mode); setPreds(r.predictions); }); }, [ws]);

  const ownerName = (id: string) => ws.users.find((u) => u.id === id)?.name ?? "—";
  const rows = useMemo(() =>
    [...preds].sort((a, b) => lens === "churn" ? b.churnRisk - a.churnRisk : b.expansion - a.expansion),
    [preds, lens]);

  const totals = useMemo(() => {
    const highRisk = preds.filter((p) => p.band === "high");
    const atRiskArr = highRisk.reduce((n, p) => n + p.arr, 0);
    const expandArr = preds.filter((p) => p.expansion >= 50).reduce((n, p) => n + p.arr, 0);
    return { highRisk: highRisk.length, atRiskArr, expandCount: preds.filter((p) => p.expansion >= 50).length, expandArr };
  }, [preds]);

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Predict</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Churn &amp; expansion</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Every account scored for churn risk and expansion propensity — explainable, driver by driver. No black box: each score shows exactly what moved it.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
          <div className="seg-control">
            <button className={lens === "churn" ? "on" : ""} onClick={() => setLens("churn")}>Churn risk</button>
            <button className={lens === "expansion" ? "on" : ""} onClick={() => setLens("expansion")}>Expansion</button>
          </div>
        </div>
      </div>

      <div className="stats">
        <div className="stat"><div className="stat__label">High-risk accounts</div><div className="stat__value" style={{ color: "var(--risk)" }}>{totals.highRisk}</div><div className="stat__foot">churn band = high</div></div>
        <div className="stat"><div className="stat__label">ARR at risk</div><div className="stat__value">{usd(totals.atRiskArr)}</div><div className="stat__foot">in high-risk accounts</div></div>
        <div className="stat"><div className="stat__label">Expansion candidates</div><div className="stat__value" style={{ color: "var(--good)" }}>{totals.expandCount}</div><div className="stat__foot">≥50% propensity</div></div>
        <div className="stat"><div className="stat__label">Expansion ARR base</div><div className="stat__value">{usd(totals.expandArr)}</div><div className="stat__foot">candidate accounts</div></div>
      </div>

      <div className="card">
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Account</th><th>Owner</th><th style={{ textAlign: "right" }}>ARR</th>
                <th style={{ width: 200 }}>Churn risk</th><th style={{ width: 200 }}>Expansion</th>
                <th>Top driver</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const driver = (lens === "churn" ? p.churnDrivers : p.expansionDrivers)[0];
                return (
                  <tr key={p.accountId}>
                    <td>
                      <div className="row" style={{ gap: 9 }}>
                        <span className={`badge badge--${bandClass(p.band)}`} style={{ padding: "3px 7px" }} title={`${p.band} churn risk`} />
                        <Link to={`/accounts/${p.accountId}`} style={{ fontWeight: 600 }}>{p.accountName}</Link>
                      </div>
                    </td>
                    <td><div className="row" style={{ gap: 7 }}><Avatar name={ownerName(p.ownerId)} size="sm" /><span style={{ fontSize: 13 }}>{ownerName(p.ownerId)}</span></div></td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{usd(p.arr)}</td>
                    <td><Bar value={p.churnRisk} color={riskColor(p.churnRisk)} /></td>
                    <td><Bar value={p.expansion} color={p.expansion >= 50 ? "var(--good)" : "var(--faint)"} /></td>
                    <td>{driver ? <span style={{ fontSize: 12.5 }}><b style={{ color: lens === "churn" ? "var(--risk)" : "var(--good)" }}>{driver.impact > 0 ? "+" : ""}{driver.impact}</b> · {driver.label}</span> : <span className="faint">—</span>}</td>
                    <td><Link className="btn btn--ghost btn--sm" to={`/accounts/${p.accountId}`}>Open</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!rows.length && <div className="card__bd"><Empty>No accounts to score yet.</Empty></div>}
      </div>
    </div>
  );
}
